import { useState, useRef } from 'react'
import { Upload, Check, Download, X } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

const formats = ['jpg', 'png', 'webp', 'gif', 'bmp']

export default function ImageConverter() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedFormat, setSelectedFormat] = useState('jpg')
  const [convertedImage, setConvertedImage] = useState(null)
  const [isConverting, setIsConverting] = useState(false)
  const fileInputRef = useRef(null)
  const canvasRef = useRef(null)

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }
    setSelectedFile(file)
    setConvertedImage(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const convertImage = () => {
    if (!selectedFile) return

    setIsConverting(true)
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)

        // Convert to selected format
        const mimeType = {
          jpg: 'image/jpeg',
          png: 'image/png',
          webp: 'image/webp',
          gif: 'image/gif',
          bmp: 'image/bmp'
        }[selectedFormat]

        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          setConvertedImage({ url, blob, format: selectedFormat })
          setIsConverting(false)
        }, mimeType, 0.95)
      }
      img.src = e.target.result
    }

    reader.readAsDataURL(selectedFile)
  }

  const downloadImage = () => {
    if (!convertedImage) return

    const link = document.createElement('a')
    link.href = convertedImage.url
    link.download = `converted-image.${convertedImage.format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const clearFile = () => {
    setSelectedFile(null)
    setConvertedImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Upload Tile */}
      <Card
        className={`cursor-pointer transition-all ${
          selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary-400'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <div className="mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-full">
                  <Upload className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Drag & drop or click to browse</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {/* Format Selection */}
      <Card>
        <Card.Body>
          <p className="text-lg font-semibold text-gray-900 mb-5 text-center">Convert To Format</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {formats.map((format) => (
              <div
                key={format}
                onClick={() => setSelectedFormat(format)}
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedFormat === format
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
              >
                {selectedFormat === format && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <p className="text-center font-semibold text-gray-900 uppercase">{format}</p>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Convert Button */}
      <Card
        className={`cursor-pointer transition-all ${
          selectedFile && !isConverting
            ? 'bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-700 hover:to-primary-900'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
        onClick={selectedFile && !isConverting ? convertImage : undefined}
      >
        <Card.Body className="text-center py-6">
          {isConverting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
              <p className="text-lg font-bold text-white">Converting...</p>
            </div>
          ) : (
            <>
              <p className="text-lg font-bold text-white mb-2">Convert Image</p>
              <p className="text-sm text-primary-100">
                {selectedFile ? 'Click to start conversion' : 'Select an image to begin'}
              </p>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Converted Image */}
      {convertedImage && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <div className="inline-block p-4 bg-gray-100 rounded-lg">
                <img src={convertedImage.url} alt="Converted" className="max-w-full h-auto max-h-96" />
              </div>
              <Button onClick={downloadImage} variant="primary" size="lg">
                <Download className="w-5 h-5 mr-2" />
                Download Converted Image
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

